import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, DownloadOutline, Heading, HStack, Text, VStack } from '@bonusly/design-system';

import {
	DESKTOP_DOWNLOAD_URLS,
	useDetectedDesktopPlatform,
	type DetectedDesktopPlatform,
} from '../../../components/bizy-side-panel/hooks/use-bizy-desktop-download';

const PLATFORM_ICON_BOX = 6;

const PlatformIconBox: FC<{ children: ReactNode }> = ({ children }) => (
	<Box alignItems="center" color="text.default" display="flex" flexShrink={0} h={PLATFORM_ICON_BOX} justifyContent="center" w={PLATFORM_ICON_BOX}>
		{children}
	</Box>
);

const AppleGlyph: FC = () => (
	<svg aria-hidden fill="currentColor" height="20" viewBox="0 0 814 1000" width="16">
		<path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
	</svg>
);

const WindowsGlyph: FC = () => (
	<svg aria-hidden fill="currentColor" height="18" viewBox="0 0 88 88" width="18">
		<path d="m0 12.402 35.687-4.86.016 34.423-35.67.203zm35.67 33.529.028 34.453L.028 75.48.026 45.7zm4.326-39.025L87.314 0v41.527l-47.318.376zm47.329 39.349-.011 41.34-47.318-6.678-.066-34.739z" />
	</svg>
);

interface DesktopCardProps {
	icon: ReactNode;
	title: string;
	detected: boolean;
	testId: string;
	children: ReactNode;
}

const DesktopCard: FC<DesktopCardProps> = ({ icon, title, detected, testId, children }) => {
	const { t } = useTranslation('bizy_settings');
	return (
		<Box border="1px solid" borderColor="border.soft" borderRadius="xl" p={4} bg="bg.surface" data-testid={testId}>
			<HStack alignItems="center" justifyContent="space-between" mb={3}>
				<HStack alignItems="center" spacing={3}>
					<PlatformIconBox>{icon}</PlatformIconBox>
					<Heading as="h4" color="text.default" size="md">
						{title}
					</Heading>
				</HStack>
				{detected && (
					<Box bg="primary.bgAlpha" color="text.default" px={2} py={0.5} borderRadius="sm">
						<Text size="sm" fontWeight="medium">
							{t('apps.detected')}
						</Text>
					</Box>
				)}
			</HStack>
			<VStack spacing={2} align="stretch">
				{children}
			</VStack>
		</Box>
	);
};

interface DownloadButtonProps {
	href: string;
	label: string;
	testId: string;
}

const DownloadButton: FC<DownloadButtonProps> = ({ href, label, testId }) => (
	<Button
		as="a"
		href={href}
		rel="noopener"
		download
		rightIcon={<DownloadOutline size="sm" />}
		variant="ghost"
		size="md"
		justifyContent="center"
		bg="secondary.bgAlpha"
		color="text.default"
		_hover={{ bg: 'secondary.bgAlphaHover' }}
		_active={{ bg: 'secondary.bgAlphaActive' }}
		data-testid={testId}
	>
		{label}
	</Button>
);

export const AppsSection: FC = () => {
	const { t } = useTranslation('bizy_settings');
	const platform: DetectedDesktopPlatform | null = useDetectedDesktopPlatform();

	const macCard = (
		<DesktopCard key="mac" icon={<AppleGlyph />} title={t('apps.macOS')} detected={platform === 'mac'} testId="bizy-apps-card-mac">
			<DownloadButton href={DESKTOP_DOWNLOAD_URLS['mac-arm64']} label={t('apps.downloadMacAppleSilicon')} testId="download-mac-arm64" />
			<DownloadButton href={DESKTOP_DOWNLOAD_URLS['mac-x64']} label={t('apps.downloadMacIntel')} testId="download-mac-x64" />
		</DesktopCard>
	);

	const windowsCard = (
		<DesktopCard key="win" icon={<WindowsGlyph />} title={t('apps.windows')} detected={platform === 'win'} testId="bizy-apps-card-win">
			<DownloadButton href={DESKTOP_DOWNLOAD_URLS['win']} label={t('apps.downloadWindows')} testId="download-win" />
		</DesktopCard>
	);

	const cards = platform === 'win' ? [windowsCard, macCard] : [macCard, windowsCard];

	return (
		<VStack spacing={4} align="stretch">
			<Text size="md" fontWeight="medium" color="text.default">
				{t('apps.desktop')}
			</Text>
			{cards}
		</VStack>
	);
};
